'use client';

import { Input, Label } from '@/components/elements';
import { ErrorLabel } from '@/components/elements/ErrorLabel';
import { useAuthModal } from '@/lib/context/AuthModalContext';
import paths from '@/paths';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { ChangeEvent, FormEvent, useState } from 'react';

interface IInputFields {
  email: string;
  password: string;
}

interface ILoginFormProps {
  handleClose: () => void;
}

export const LoginForm = ({ handleClose }: ILoginFormProps) => {
  const router = useRouter();
  const { userType } = useAuthModal();
  const [errors, setErrors] = useState<Record<keyof IInputFields, boolean>>({ email: false, password: false });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputs, setInputs] = useState<IInputFields>(() => ({ email: '', password: '' }));

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputs((prevValue) => ({ ...prevValue, [e.target.name]: e.target.value }));
    setErrors((prevValue) => ({ ...prevValue, [e.target.name]: false }));
    setErrorMessage(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    // Basic validation
    let hasErrors = false;
    if (!inputs.email || !inputs.email.includes('@')) {
      setErrors((prev) => ({ ...prev, email: true }));
      hasErrors = true;
    }

    if (!inputs.password || inputs.password.length < 6) {
      setErrors((prev) => ({ ...prev, password: true }));
      hasErrors = true;
    }

    if (hasErrors) {
      setIsLoading(false);
      return;
    }

    const result = await signIn("credentials", {
      email: inputs.email,
      password: inputs.password,
      redirect: false, // Don't redirect, just handle in the modal
    });

    setIsLoading(false);

    if (result?.error) {
      console.error('Login error:', result?.error);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } else {
      handleClose();
      if (userType === 'provider') router.push(paths.registerTourOperater());
      router.refresh();
    }
  };

  return (
    <>
      <div className="login-modal__body flex flex-col gap-6 px-6 pt-6 pb-8">
        <img src="/static/images/logo.png" alt="FaunaExplore logo" height={42} width={37} />
        <h3 className="text-xl font-semibold font-rokkitt">Sign in to FaunaExplore</h3>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{errorMessage}</div>
        )}

        <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col flex-wrap gap-1">
              <Label>Email address</Label>
              <Input.Controlled
                name="email"
                value={inputs.email}
                handleChange={handleChange}
                placeholder="Type here..."
                isInvalid={errors.email}
              />
              {errors.email && <ErrorLabel>Please enter a valid email address</ErrorLabel>}
            </div>

            <div className="flex flex-col flex-wrap gap-1">
              <Label>Password</Label>
              <Input.Password
                name="password"
                value={inputs.password}
                handleChange={handleChange}
                placeholder="Type here..."
                isInvalid={errors.password}
              />
              {errors.password && <ErrorLabel>Password must be at least 6 characters</ErrorLabel>}
            </div>

            <span className="text-sm font-semibold cursor-pointer">Forgot your password?</span>
          </div>
          <button className="btn w-full" type="submit" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </>
  );
};
